import { StyleSheet } from "react-native";
import Colors from "../../../../Resources/Color";

// ============================================================================
// Constants
// ============================================================================

export const DROP_INDICATOR_HEIGHT = 3;

export const BLOCKED_MOVE_TOAST_KEY = "protocolMoveWeekLimit-string";
export const BLOCKED_MOVE_TOAST_DESC_KEY = "protocolMoveWeekLimitDescription-string";

// ============================================================================
// Global State
// ============================================================================

let activeBlockedMoveToastId = null;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Normalize protocol slot string to a trimmed string or null.
 * 
 * Returns null if:
 * - protocolSlot is null, undefined, or not a string
 * - protocolSlot is an empty string or whitespace-only after trimming
 * 
 * Returns trimmed string if protocolSlot is a non-empty string.
 */
export const normalizeProtocolSlot = (protocolSlot) => {
  // Explicitly check for null/undefined first
  if (protocolSlot == null) return null;
  
  // Must be a string type
  if (typeof protocolSlot !== "string") return null;
  
  // Trim and check if result is non-empty
  const trimmed = protocolSlot.trim();
  return trimmed || null; // Empty string becomes null
};

/**
 * Check if a cross-week move is blocked for protocol slot exercises.
 * Protocol slot exercises can only be moved within the same week.
 * 
 * Returns false (not blocked) if:
 * - item is falsy
 * - protocolSlot is null, undefined, empty string, or whitespace-only
 * - sourceWeek or targetWeek is null/undefined
 * - sourceWeek equals targetWeek (same week)
 * 
 * Returns true (blocked) only if:
 * - protocolSlot is a non-empty string AND
 * - sourceWeek !== targetWeek (different weeks)
 * 
 * CRITICAL: This function MUST return false for exercises without protocol slots.
 * Never show the protocol slot warning for exercises with null/undefined/empty protocolSlot.
 */
export const isCrossWeekBlocked = (item, targetWeek) => {
  // Early return if item is falsy
  if (!item) return false;
  
  // CRITICAL: Check protocolSlot directly first - if it's null/undefined, immediately return false
  // This prevents any edge cases where normalization might fail
  if (item.protocolSlot == null) return false;
  
  // Normalize and check protocol slot - must be a non-empty string
  // This is the critical check: if there's no protocol slot, the move is never blocked
  const slot = normalizeProtocolSlot(item.protocolSlot);
  
  // Multiple defensive checks to ensure we never block when there's no protocol slot
  if (slot == null) return false; // Normalized to null = not blocked
  if (slot === "") return false; // Empty string = not blocked
  if (typeof slot !== "string") return false; // Not a string = not blocked
  if (slot.length === 0) return false; // Zero length = not blocked
  if (slot.trim().length === 0) return false; // Whitespace only = not blocked
  
  // Both weeks must be defined and different for it to be blocked
  if (item.sourceWeek == null || targetWeek == null) return false;
  if (item.sourceWeek === targetWeek) return false; // Same week = not blocked
  
  // Only blocked if protocol slot exists (non-empty string) AND weeks are different
  // At this point, we've verified slot is a non-empty string, so it's safe to return true
  return true;
};

/**
 * Get the actual DOM element from a React Native Web ref.
 * Handles various ref types (direct DOM, _component, getNode, etc.)
 */
export const getDOMElement = (ref) => {
  if (!ref) return null;
  if (ref.getBoundingClientRect) return ref;
  if (ref._component) return ref._component;
  if (typeof ref.getNode === "function") return ref.getNode();
  return ref;
};

/**
 * Calculate drop position (above or below) based on pointer Y and element rect.
 * Returns "above" if pointerY < elementRect.top + elementRect.height / 2
 * Returns "below" otherwise
 */
export const calculateDropPosition = (pointerY, elementRect) => {
  if (!elementRect || pointerY === undefined || pointerY === null) return null;
  const middleY = elementRect.top + elementRect.height / 2;
  return pointerY < middleY ? "above" : "below";
};

/**
 * Show toast notification for blocked cross-week moves
 */
export const showBlockedMoveToast = (showToast) => {
  if (activeBlockedMoveToastId) return;
  activeBlockedMoveToastId = showToast(BLOCKED_MOVE_TOAST_KEY, {
    type: "error",
    description: BLOCKED_MOVE_TOAST_DESC_KEY,
  });
};

/**
 * Clear the blocked move toast notification
 */
export const clearBlockedMoveToast = (hideToast) => {
  if (!activeBlockedMoveToastId) return;
  hideToast(activeBlockedMoveToastId);
  activeBlockedMoveToastId = null;
};

/**
 * Execute move logic based on drop data.
 * Handles both same-workout reordering and cross-workout moves.
 * 
 * @param {Object} activeData - Data from dragged item
 * @param {Object} overData - Data from drop target
 * @param {Function} onMoveExercise - Callback for same-workout moves
 * @param {Function} onMoveExerciseCrossWorkout - Callback for cross-workout moves
 * @param {string|null} dropPosition - "above" | "below" | null (for exerciseTarget drops)
 * @returns {string|string[]|null} - Moved exercise ID(s) or null
 */
export const executeMove = (
  activeData,
  overData,
  onMoveExercise,
  onMoveExerciseCrossWorkout,
  dropPosition = null,
) => {
  if (!activeData || !overData) return null;

  const sourceWorkoutId = activeData.workoutId;
  const targetWorkoutId = overData.workoutId;
  const sourceIndex = activeData.index;

  // Determine target position based on drop zone type
  let targetPosition = overData.position ?? overData.index ?? 0;

  // Handle different drop zone types
  const dropType = overData.type;

  if (dropType === "exerciseTarget") {
    // Dropped on an exercise - use dropPosition to determine above/below
    if (dropPosition === "above") {
      // Insert above (at this index)
      targetPosition = overData.index;
    } else {
      // Insert below (after this index)
      targetPosition = overData.index + 1;
    }
  } else if (dropType === "exerciseDropZone") {
    // Dropped in a gap between exercises
    targetPosition = overData.position;
  } else if (dropType === "trailingDropZone") {
    // Dropped at the end
    targetPosition = overData.position;
  } else if (dropType === "emptyWorkoutDropZone") {
    // Dropped in empty workout
    targetPosition = 0;
  }

  // Execute move and return moved exercise ID(s)
  if (sourceWorkoutId === targetWorkoutId) {
    // Same workout - reorder
    // Adjust target index if source is before target
    let adjustedTarget = targetPosition;
    if (sourceIndex < targetPosition) {
      adjustedTarget -= 1;
    }

    if (sourceIndex !== adjustedTarget && onMoveExercise) {
      return onMoveExercise(
        sourceWorkoutId,
        sourceIndex,
        adjustedTarget,
        activeData.exerciseId,
      );
    }
  } else {
    // Cross-workout move
    if (onMoveExerciseCrossWorkout) {
      return onMoveExerciseCrossWorkout(
        sourceWorkoutId,
        activeData.exerciseId,
        targetWorkoutId,
        targetPosition,
      );
    }
  }

  return null;
};

// ============================================================================
// Shared Styles
// ============================================================================

/**
 * Shared drop indicator styles used by both @dnd-kit and react-dnd implementations.
 * Ensures visual consistency across platforms.
 */
export const dropIndicatorStyles = StyleSheet.create({
  dropIndicator: {
    position: "absolute",
    left: 4,
    right: 4,
    height: DROP_INDICATOR_HEIGHT,
    backgroundColor: Colors.bubbleColor,
    borderRadius: 3,
    zIndex: 100,
    transition: "opacity 0.2s ease-in-out",
  },
  dropIndicatorCircle: {
    position: "absolute",
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.bubbleColor,
    top: -2.5,
  },
  dropIndicatorCircleLeft: {
    left: -4,
  },
  dropIndicatorCircleRight: {
    right: -4,
  },
  // Empty workout drop zone
  emptyDropZone: {
    flex: 1,
    minHeight: 120,
    borderRadius: 8,
  },
  dropZoneActive: {
    backgroundColor: Colors.lightGrayColor,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    borderStyle: "dashed",
    borderRadius: 8,
    transition: "opacity 0.2s ease-in-out",
  },
  // Trailing drop zone
  trailingDropWrapper: {
    position: "absolute",
    top: 0,
    left: -4,
    right: -4,
    bottom: 0, // Fill the entire spacer height
    pointerEvents: "none", // Disable pointer events on wrapper (child re-enables them)
  },
  trailingDropHitArea: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0, // Fill the entire wrapper (which fills the spacer)
    pointerEvents: "auto", // Re-enable pointer events for drop detection
  },
});
